import React, { Fragment } from "react";

import { Trans, useTranslation } from "react-i18next";
import Select from "react-select";
import { ErrorAlert } from "../../utils/Alerts";

import {
    getReactSelectSmallStyling,
    getReactSelectTheme,
    getShortSelectMenu,
} from "../../utils/ReactSelectStyles";
import ValidationError from "../Shared/ValidationError";
import InformationTooltip from "../Tooltips/InformationTooltip";

const ParameterUnitsCards = ({
    parameter,
    parameterUnits,
    handleParameterDetailsChange,
    handleUnitsChange,
    removeUnitHandler,
    parameterIndex,
    units,
    conversionUnits,
    activeUnit,
    setActiveUnit,
    error,
    isFormSubmitted,
    isEdit,
    isUnitsLoading,
    conversionUnitsLoading,
    parameterUnitsMaps,
    isDynamicSorted,
}) => {
    const { t } = useTranslation();

    const isBaseUnit = (index) => {
        return index === 0;
    };

    const isUnitClosable = (index) => {
        return !isBaseUnit(index) && parameterUnits.length > 1;
    };

    const handleActiveUnitChange = (index) => {
        const condition = isEdit
            ? !isDynamicSorted
            : !parameterUnitsMaps?.[parameter.name];

        if (condition) {
            setActiveUnit((prevUnit) => {
                const newUnits = [...prevUnit];
                newUnits[parameterIndex] = index;
                return newUnits;
            });
        } else {
            ErrorAlert(t("unchangableUnitMessage"));
        }
    };

    const getFormContent = (parameterUnit = {}, index) => {
        const isCurrentBaseUnit = isBaseUnit(index);
        const isSelectLoading = isCurrentBaseUnit
            ? isUnitsLoading
            : conversionUnitsLoading;
        return (
            <div
                className={`item-list ${
                    isCurrentBaseUnit ? "orange-list" : ""
                } mb-4`}
            >
                <div className="flex justify-between align-center">
                    <div className="input-group w-full">
                        <div className="tags px-2 py-2 whitespace-wrap">
                            <h4 className="uppercase text-xs required">
                                <Trans i18nKey="unit">Unit</Trans>
                            </h4>
                            <Select
                                options={
                                    isCurrentBaseUnit
                                        ? units
                                        : conversionUnits[parameterIndex]
                                }
                                value={parameterUnit.unit || ""}
                                onChange={(val) =>
                                    handleUnitsChange(
                                        parameterIndex,
                                        index,
                                        val,
                                        isCurrentBaseUnit
                                    )
                                }
                                placeholder={t("unit")}
                                isDisabled={
                                    (isEdit && isCurrentBaseUnit) ||
                                    isSelectLoading
                                }
                                isLoading={isSelectLoading}
                                styles={getReactSelectSmallStyling(false)}
                                theme={getReactSelectTheme}
                                components={getShortSelectMenu()}
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={
                                        error[
                                            `${parameterIndex.toString()}.parameterDetails.${index.toString()}.unit`
                                        ]
                                    }
                                />
                            )}
                        </div>
                    </div>
                    <div className="input-group w-full">
                        <div className="tags px-2 py-2 whitespace-wrap">
                            <h4 className="uppercase text-xs required">
                                <Trans i18nKey="decimalPlaces">
                                    Decimal Places
                                </Trans>
                            </h4>
                            <input
                                className="input-element small-input"
                                type="number"
                                name="decimalplaces"
                                min="0"
                                max="4"
                                value={parameterUnit.decimalPlaces ?? ""}
                                onChange={(e) =>
                                    handleParameterDetailsChange(
                                        parameterIndex,
                                        index,
                                        "decimalPlaces",
                                        e.target.value || e.target.value === 0
                                            ? +e.target.value
                                            : ""
                                    )
                                }
                                placeholder={t("decimalPlacesPlaceholder")}
                            />
                            {isFormSubmitted && (
                                <ValidationError
                                    errorMessage={
                                        error[
                                            `${parameterIndex.toString()}.parameterDetails.${index.toString()}.decimalPlaces`
                                        ]
                                    }
                                />
                            )}
                        </div>
                    </div>
                    {!isCurrentBaseUnit && (
                        <div className="input-group">
                            <div className="tags px-2 py-2 whitespace-wrap">
                                <div className="flex">
                                    <h4 className="uppercase text-xs h-6 mr-1">
                                        <Trans i18nKey="active">active</Trans>
                                    </h4>
                                    <InformationTooltip
                                        message={t("activeUnitDescription")}
                                        className="text-xxs mt-1"
                                    />
                                </div>
                                <div className="checkbox-wrapper-input">
                                    <input
                                        onChange={() =>
                                            handleActiveUnitChange(index)
                                        }
                                        type="checkbox"
                                        className="checkbox-element"
                                        id={`isActiveUnit-${index}-${parameterIndex}`}
                                        name={`isActiveUnit-${index}-${parameterIndex}`}
                                        value={
                                            index ===
                                                activeUnit[parameterIndex] || ""
                                        }
                                        checked={
                                            index === activeUnit[parameterIndex]
                                        }
                                    />
                                    <label
                                        htmlFor={`isActiveUnit-${index}-${parameterIndex}`}
                                    ></label>
                                </div>
                            </div>
                        </div>
                    )}
                    {isUnitClosable(index) && (
                        <div className="icon-wrapper text-xs mt-1">
                            <i
                                className="fas fa-times cursor-pointer"
                                onClick={() =>
                                    removeUnitHandler(parameterIndex, index)
                                }
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderUnitsForm = () => {
        return (
            parameterUnits &&
            parameterUnits.map((parameterUnit, index) => {
                return (
                    <Fragment key={index}>
                        {getFormContent(parameterUnit, index)}
                    </Fragment>
                );
            })
        );
    };

    return renderUnitsForm();
};

export default ParameterUnitsCards;
